import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'

import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import { INITAL_CLUBBED_AUTH_MODAL_BASIC_DETAILS_STATE } from '../SignIn/ClubbedAuthComponents/utils'
import { setLocalStorageOnboardingType } from '../../../mentor/layout/utils'
import './AumhumSite.css'
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Repeat,
  ShieldCheck,
  UserCheck,
  Workflow,
} from './icons'
import {
  findCategoryHub,
  findRoleBySlug,
  JOIN_CATEGORIES,
  JOIN_CATEGORY_HUBS,
  JOIN_ROLES,
  LEGACY_TYPE_TO_SLUG,
} from './joinData'
import { Footer, TopBar } from './Shell'
import {
  Card,
  Container,
  Eyebrow,
  IconBadge,
  Lede,
  SecondaryButton,
  SectionTitle,
} from './sections'

const CANONICAL_ORIGIN = 'https://aumhum.com'

// ── Apply CTA ─────────────────────────────────────────────
// The "Apply now" button captures the role slug in localStorage (so the
// authenticated /mentors/apply surface knows what to render), sets the coarse
// onboarding type for the legacy flow, drops a session flag so the mentor
// join modal auto-opens after reload, and opens the auth modal in signup
// mode.

function startApply({ role, setAuthOpen }) {
  try {
    if (role?.category) setLocalStorageOnboardingType(role.category)
    if (role?.slug) localStorage.setItem('mentorJoinRoleSlug', role.slug)
    sessionStorage.setItem('openMentorJoinModal', 'true')
  } catch (e) {
    // storage may be blocked in some contexts; non-fatal
  }
  setAuthOpen(true)
}

// ── Role-specific view ────────────────────────────────────

const RoleView = ({ role, onApply }) => (
  <main id="main">
    <section className="as-hero" aria-labelledby="join-role-title">
      <Container>
        <div className="as-hero-grid">
          <div>
            <Eyebrow>{role.hero.eyebrow}</Eyebrow>
            <h1 id="join-role-title" className="as-h1">
              {role.hero.title}
            </h1>
            <p className="as-lede">{role.hero.lede}</p>
            <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
              <button
                type="button"
                className="as-btn as-btn-primary"
                onClick={onApply}
              >
                Apply to join <ArrowRight />
              </button>
              <SecondaryButton to="/how-care-works">How care works</SecondaryButton>
              <Link to="/join" className="as-link">
                Explore other roles →
              </Link>
            </div>
            <ul
              className="as-mt-8 as-flex as-wrap as-gap-6"
              aria-label="What to expect"
            >
              <li className="as-trust-item">
                <ShieldCheck /> Clinical supervision
              </li>
              <li className="as-trust-item">
                <Repeat /> Continuous care
              </li>
              <li className="as-trust-item">
                <Workflow /> Shared clinical record
              </li>
            </ul>
          </div>
          <div>
            <div className="as-pathway" aria-label="Application pathway">
              <p className="as-pathway-label">Application pathway</p>
              <ol className="as-pathway-list">
                {[
                  { Icon: ClipboardList, title: 'Apply online', sub: 'Quick profile with your qualifications and availability.' },
                  { Icon: UserCheck, title: 'Clinical interview', sub: "Short conversation with AUMHUM's clinical team." },
                  { Icon: ShieldCheck, title: 'Credentialing', sub: 'Registration and verification under clinical governance.' },
                  { Icon: Repeat, title: 'Onboarding', sub: 'Access to tooling, shared clinical record, and supervision.' },
                ].map(({ Icon, title, sub }) => (
                  <li key={title} className="as-pathway-item">
                    <div className="as-icon-circle">
                      <Icon />
                    </div>
                    <div>
                      <p className="as-pathway-item-title">{title}</p>
                      <p className="as-pathway-item-sub">{sub}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="as-pathway-footer">
                <Workflow /> Most applicants hear back within five working days.
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>

    <section className="as-section as-section-white">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Day-to-day</Eyebrow>
          <SectionTitle>What the role involves</SectionTitle>
          <Lede>{role.tagline}</Lede>
        </div>
        <ul className="as-mt-10 as-bullet-cards">
          {role.responsibilities.map((r) => (
            <li key={r} className="as-bullet-card">
              <CheckCircle2 /> {r}
            </li>
          ))}
        </ul>
      </Container>
    </section>

    <section className="as-section as-section-alt">
      <Container>
        <div className="as-max-w-3">
          <Eyebrow>Why join AUMHUM</Eyebrow>
          <SectionTitle>
            A clinical system — not another directory listing
          </SectionTitle>
          <Lede>
            You join a coordinated team of clinicians, not a marketplace. Every role at AUMHUM
            plugs into the same continuous care system: a shared clinical record, measurement,
            supervision, and integrated psychiatric, medical, and endocrine context.
          </Lede>
        </div>
        <div className="as-mt-12 as-grid as-grid-2">
          {role.whyJoin.map(({ Icon, title, body }) => (
            <Card key={title}>
              <IconBadge Icon={Icon} />
              <h3 className="as-h3 as-mt-4">{title}</h3>
              <p className="as-p as-mt-2">{body}</p>
            </Card>
          ))}
        </div>
      </Container>
    </section>

    <section className="as-section as-section-white">
      <Container>
        <div className="as-grid-12">
          <div className="as-col-6">
            <Eyebrow>Requirements</Eyebrow>
            <SectionTitle>What we look for</SectionTitle>
            <ul
              className="as-mt-6"
              style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}
            >
              {role.requirements.map((r) => (
                <li key={r} className="as-check-card">
                  <CheckCircle2 /> {r}
                </li>
              ))}
            </ul>
          </div>
          <div className="as-col-6">
            <Eyebrow>Engagement</Eyebrow>
            <SectionTitle>Work where it fits</SectionTitle>
            <p className="as-p as-mt-6">{role.engagement}</p>
            <p className="as-p as-mt-3">
              Prefer to learn more about how care is delivered first? See{' '}
              <Link to="/how-care-works" className="as-link as-link-violet">
                how care works
              </Link>
              , or browse the{' '}
              <Link to="/students" className="as-link as-link-violet">
                AUMHUM Experience Program
              </Link>{' '}
              if you&apos;re still in training.
            </p>
          </div>
        </div>
      </Container>
    </section>

    <section className="as-cta-final">
      <Container>
        <div className="as-grid-12 as-items-center">
          <div className="as-col-8">
            <Eyebrow dark>Ready to join?</Eyebrow>
            <h2 className="as-h2" style={{ color: '#fff' }}>
              {role.label} — inside a clinical system.
            </h2>
            <p className="as-lede">
              Share your profile and our clinical team will reach out within five working days.
            </p>
          </div>
          <div className="as-col-4 as-flex as-wrap as-gap-3 as-justify-end">
            <button
              type="button"
              className="as-btn as-btn-light"
              onClick={onApply}
            >
              Apply to join <ArrowRight />
            </button>
          </div>
        </div>
      </Container>
    </section>
  </main>
)

// ── Category hub view (e.g. /join/doctor) ─────────────────

const CategoryHubView = ({ hub }) => {
  const roles = hub.roleSlugs
    .map((s) => JOIN_ROLES.find((r) => r.slug === s))
    .filter(Boolean)
  return (
    <main id="main">
      <section className="as-hero" aria-labelledby="join-hub-title">
        <Container>
          <Eyebrow>{hub.eyebrow}</Eyebrow>
          <h1 id="join-hub-title" className="as-h1 as-max-w-4">
            {hub.label}
          </h1>
          <p className="as-lede">{hub.lede}</p>
        </Container>
      </section>
      <section className="as-section as-section-white">
        <Container>
          <div className="as-max-w-3">
            <Eyebrow>Choose your role</Eyebrow>
            <SectionTitle>Pick the role that matches your practice</SectionTitle>
          </div>
          <ul className="as-mt-12 as-grid as-grid-2">
            {roles.map((r) => (
              <li key={r.slug}>
                <Card>
                  <div className="as-flex as-items-center as-gap-3">
                    <IconBadge Icon={r.Icon} />
                    <h3 className="as-h3">{r.label}</h3>
                  </div>
                  <p className="as-p as-mt-3">{r.tagline}</p>
                  <div className="as-mt-6">
                    <Link to={`/join/${r.slug}`} className="as-btn as-btn-secondary">
                      Continue as {r.label} <ArrowRight />
                    </Link>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </Container>
      </section>
    </main>
  )
}

// ── /join index: all roles ────────────────────────────────

const JoinIndexView = () => {
  const doctors = JOIN_ROLES.filter((r) => r.category === JOIN_CATEGORIES.DOCTOR)
  const therapists = JOIN_ROLES.filter(
    (r) => r.category === JOIN_CATEGORIES.THERAPIST,
  )
  return (
    <main id="main">
      <section className="as-hero" aria-labelledby="join-index-title">
        <Container>
          <Eyebrow>Join AUMHUM</Eyebrow>
          <h1 id="join-index-title" className="as-h1 as-max-w-4">
            Clinical roles inside a continuous care system.
          </h1>
          <p className="as-lede">
            Work alongside therapists, psychiatrists, endocrinologists, and allied
            professionals — inside a shared clinical record, with supervision and
            measurement-based care built in.
          </p>
          <div className="as-mt-8 as-flex as-wrap as-items-center as-gap-3">
            <SecondaryButton to="/how-care-works">How care works</SecondaryButton>
            <Link to="/students" className="as-link">
              Students — AUMHUM Experience Program →
            </Link>
          </div>
        </Container>
      </section>

      <RoleGrid title="For doctors" eyebrow="Doctors" roles={doctors} />
      <RoleGrid
        title="For therapists &amp; care-givers"
        eyebrow="Care-Givers"
        roles={therapists}
        alt
      />
    </main>
  )
}

const RoleGrid = ({ title, eyebrow, roles, alt = false }) => (
  <section className={`as-section ${alt ? 'as-section-alt' : 'as-section-white'}`}>
    <Container>
      <div className="as-max-w-3">
        <Eyebrow>{eyebrow}</Eyebrow>
        <SectionTitle>{title}</SectionTitle>
      </div>
      <ul className="as-mt-12 as-grid as-grid-2">
        {roles.map((r) => (
          <li key={r.slug}>
            <Card>
              <div className="as-flex as-items-center as-gap-3">
                <IconBadge Icon={r.Icon} />
                <h3 className="as-h3">{r.label}</h3>
              </div>
              <p className="as-p as-mt-3">{r.tagline}</p>
              <div className="as-mt-6">
                <Link to={`/join/${r.slug}`} className="as-btn as-btn-secondary">
                  Learn more &amp; apply <ArrowRight />
                </Link>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </Container>
  </section>
)

// ── SEO head ──────────────────────────────────────────────

const JoinHelmet = ({ pathname, kind, role, hub }) => {
  const meta = useMemo(() => {
    if (kind === 'role' && role) {
      return { title: role.seoTitle, description: role.seoDescription }
    }
    if (kind === 'hub' && hub) {
      return { title: hub.seoTitle, description: hub.seoDescription }
    }
    return {
      title: 'Join AUMHUM — clinical roles in continuous mental healthcare',
      description:
        'Clinical roles at AUMHUM — psychiatrists, therapists, endocrinologists, clinical psychologists, pediatricians, nutritionists and more, inside a coordinated care system.',
    }
  }, [kind, role, hub])

  const canonical = `${CANONICAL_ORIGIN}${pathname}`
  const image = APP_CONSTANTS.APP_LOGO_LINK

  const jsonLd = useMemo(() => {
    if (kind === 'role' && role) {
      return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: role.label,
        description: role.hero.lede,
        hiringOrganization: {
          '@type': 'Organization',
          name: 'AUMHUM',
          sameAs: CANONICAL_ORIGIN,
          logo: image,
        },
        employmentType: ['FULL_TIME', 'PART_TIME'],
        jobLocationType: 'TELECOMMUTE',
        url: canonical,
        qualifications: role.requirements?.join('; '),
        responsibilities: role.responsibilities?.join('; '),
        datePosted: new Date().toISOString().slice(0, 10),
      }
    }
    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      url: canonical,
      name: meta.title,
      description: meta.description,
    }
  }, [kind, role, canonical, meta, image])

  return (
    <Helmet>
      <html lang="en" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta
        name="keywords"
        content="AUMHUM careers, mental health jobs, psychiatrist jobs, therapist jobs, endocrinologist jobs, clinical psychology careers, India"
      />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <link rel="preconnect" href="https://media.aumhum.com" crossOrigin="" />

      <meta property="og:site_name" content="AUMHUM" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={image} />

      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

// ── Root ──────────────────────────────────────────────────

export default function JoinPage({ view = 'role' }) {
  // All hooks must run in a stable order — defined once at the top, before
  // any conditional early returns (react-hooks/rules-of-hooks).
  const location = useLocation()
  const { slug: routeSlug } = useParams()
  const [authOpen, setAuthOpen] = useState(false)

  const slugFromQuery = useMemo(() => {
    if (view !== 'legacy') return null
    const params = new URLSearchParams(location.search)
    const legacy = (params.get('type') || '').toLowerCase().trim()
    return LEGACY_TYPE_TO_SLUG[legacy] || null
  }, [view, location.search])

  const role = useMemo(
    () => (view === 'role' ? findRoleBySlug(routeSlug) : null),
    [view, routeSlug],
  )
  const hub = useMemo(
    () => (view === 'role' && !role ? findCategoryHub(routeSlug) : null),
    [view, role, routeSlug],
  )

  useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo(0, 0)
  }, [location.pathname])

  // Legacy route redirect: /mentors/join?type=doctor → /join/doctor, etc.
  if (view === 'legacy') {
    const target = slugFromQuery ? `/join/${slugFromQuery}` : '/join'
    return <Navigate to={target} replace />
  }

  // Unknown slug → soft redirect to the index
  if (view === 'role' && routeSlug && !role && !hub) {
    return <Navigate to="/join" replace />
  }

  const kind = role ? 'role' : hub ? 'hub' : 'index'

  let viewEl = <JoinIndexView />
  if (role) {
    viewEl = (
      <RoleView
        role={role}
        onApply={() => startApply({ role, setAuthOpen })}
      />
    )
  } else if (hub) {
    viewEl = <CategoryHubView hub={hub} />
  }

  return (
    <div className="aumhum-site-root">
      <JoinHelmet pathname={location.pathname} kind={kind} role={role} hub={hub} />
      <a href="#main" className="as-skip-link">
        Skip to content
      </a>
      <TopBar onLogin={() => setAuthOpen(true)} />
      {viewEl}
      <Footer />
      <ClubbedAuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        loginVariant={false}
        nameFieldHeading="Your Name"
        initialOnboardingDetails={{
          ...INITAL_CLUBBED_AUTH_MODAL_BASIC_DETAILS_STATE,
          name:
            role?.category === JOIN_CATEGORIES.DOCTOR ||
            JOIN_CATEGORY_HUBS.doctor?.roleSlugs?.includes(role?.slug)
              ? 'Dr. '
              : '',
        }}
        beforeReloadFunction={() => {
          try {
            sessionStorage.setItem('openMentorJoinModal', 'true')
          } catch (e) {
            /* ignore */
          }
        }}
      />
    </div>
  )
}
